import { useEffect, useState } from 'react';
import { fill } from '@/copy';
import { toast } from '@/lib/toast';
import { Button } from '@/ui';
import { copy } from './copy';
import { exportCardPng } from './export';
import { plateName } from './model';
import type { PlayerCardProps } from './PlayerCard';
import { shareImage } from './share';

/**
 * "Share card": builds the 1080x1512 PNG as soon as it mounts and calls the share sheet straight
 * from the tap (navigator.share needs the gesture, and the PNG takes longer than the gesture
 * lasts). Falls back to a download. Profile and studio both put it under their L card.
 */
export interface ShareCardButtonProps {
  card: Omit<PlayerCardProps, 'size' | 'onPress' | 'revealState'>;
  label?: string;
  className?: string;
}

/** `tts-card-naz.png`: the file name for a player, ASCII-safe (Greek names become `player`). */
export function cardFileName(name: string): string {
  const slug = name
    .normalize('NFKD')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();
  return fill(copy.share.fileName, { name: slug || copy.share.fallbackName });
}

export function ShareCardButton({ card, label = copy.share.button, className }: ShareCardButtonProps) {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [failed, setFailed] = useState(false);
  const { player, rating, portrait, preset } = card;
  // Rebuild when the card changes (a new rating, a new portrait), not on every render.
  const key = [
    player.id,
    rating?.asOf ?? '',
    rating?.ovr ?? '',
    portrait?.portraitId ?? player.portrait?.portraitId ?? '',
    preset ?? '',
  ].join('|');

  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for the card
  useEffect(() => {
    let live = true;
    setBlob(null);
    setFailed(false);
    exportCardPng({ ...card, size: 'l' })
      .then((b) => live && setBlob(b))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [key]);

  async function onTap() {
    if (!blob) return;
    const outcome = await shareImage(blob, cardFileName(plateName(player)));
    if (outcome === 'downloaded') toast({ title: copy.share.downloaded, tone: 'success' });
  }

  return (
    <Button
      variant="accent"
      icon="share"
      loading={!blob && !failed}
      loadingLabel={copy.share.working}
      onClick={failed ? () => toast({ title: copy.share.failed, tone: 'danger' }) : onTap}
      className={className}
      data-testid="share-card"
    >
      {label}
    </Button>
  );
}
